import React, { Component, Fragment, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";
import { Input, Dialog, Slide } from "@mui/material";
import { compose } from "recompose";
import "views/Pma/PmaEditor/CommonEditor/common.scss";
import { withSnackbar } from "notistack";
import classNames from "classnames";
import { showSuccessNotification } from "common/helpers";
import { showErrorNotification } from "common/helpers";
import { AddUpdatePmaAdmin } from "services/agent";

import { propertyUtil } from "views/Pma/PropertyUtil";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const REPRESENTATION_TEXT_MAX_LENGTH = 50;

const alternatePhotoButtonStyle = {
  padding: "8px 16px",
  backgroundColor: "#e8860a",
  color: "white",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "bold",
  transition: "opacity 0.15s ease-in-out",
  outline: "none",
  boxShadow: "none",
};

const PropHighlightsEditProperty = (props) => {
  const {
    //representedOptions,
    editPropFormData,
    handleInputChange,
    openUploadMedia,
    onCloseFn,
    oldPropData,
    areaID,
    onEditFormSubmit,
    fromPhotoTable,
    agentData,
    handleStatusChange,
    selectedStatus,
    onReplaceProperty,
    showAllPropertiesButton = false,
    showButton = true,
  } = props;

  // State to track if the form is editable
  const [isFormEditable, setIsFormEditable] = useState(false);
  const [isReplaceMode, setIsReplaceMode] = useState(false);
  const [isBuyerRepresented, setIsBuyerRepresented] = useState(false);
  const [buyerPermissionType, setBuyerPermissionType] = useState("");
  const [hasTriedToSubmitBuyerPermission, setHasTriedToSubmitBuyerPermission] =
    useState(false);

  // State for button flashing animation
  const [isFlashing, setIsFlashing] = useState(false);

  // Flash buttons 5 times when modal opens
  useEffect(() => {
    let flashCount = 0;
    const maxFlashes = 5;
    const flashInterval = setInterval(() => {
      setIsFlashing((prev) => !prev);
      flashCount++;
      if (flashCount >= maxFlashes * 2) {
        // *2 because we toggle on/off
        clearInterval(flashInterval);
        setIsFlashing(false);
      }
    }, 600); // Flash every 600ms (2x slower)
    return () => clearInterval(flashInterval);
  }, []);

  // Check if property indicates buyer representation and set checkbox accordingly
  useEffect(() => {
    const currentFooterText = editPropFormData.footerText1 || "";

    // Check if this is "Sellers only" representation (not "Buyers & Sellers")
    const hasSellers = currentFooterText.includes("Seller");
    const hasBuyers = currentFooterText.includes("Buyer");
    const isSellersOnly = hasSellers && !hasBuyers;

    // If it's sellers only, override any old flags and set to false
    if (isSellersOnly) {
      setIsBuyerRepresented(false);
      return;
    }

    const buyerPatterns = [
      "We Represented the Buyers",
      "We Represented the Buyer",
      "I Represented the Buyer",
      "I Represented the Buyers",
      "We Are Representing the Buyers",
      "I Am Representing the Buyers",
    ];

    const isBuyerRepresentedInData =
      editPropFormData.isBuyingAgent === true ||
      editPropFormData.checkedBuyerRepresented === true ||
      buyerPatterns.some((pattern) => currentFooterText.includes(pattern));

    setIsBuyerRepresented(isBuyerRepresentedInData);
  }, [
    editPropFormData.isBuyingAgent,
    editPropFormData.checkedBuyerRepresented,
    editPropFormData.footerText1,
  ]);

  // Auto-select radio button based on existing buyerRepresentedReason
  useEffect(() => {
    if (editPropFormData.buyerRepresentedReason) {
      setBuyerPermissionType(editPropFormData.buyerRepresentedReason);
    }
  }, [editPropFormData.buyerRepresentedReason]);

  const {
    isMemberOfTeam = false,
    agentLicenseNumber = null,
    teamMembers = [],
    brokerageName = "",
  } = agentData || {};
  const teamMember1License =
    isMemberOfTeam && teamMembers[0]?.agentLicenseNumber;
  const teamMember2License =
    isMemberOfTeam && teamMembers[1]?.agentLicenseNumber;

  const { formatSuffix } = propertyUtil;

  // Construct banner text values
  const constructBannerText1 = () => {
    const streetNumber =
      oldPropData.streetNumber === "-" || oldPropData.streetNumber === "0"
        ? ""
        : oldPropData.streetNumber;
    const streetName = oldPropData.streetName || "";
    const streetSuffix =
      oldPropData.streetSuffix && oldPropData.streetSuffix !== "-"
        ? formatSuffix(oldPropData.streetSuffix, areaID)
        : "";
    return `${streetNumber} ${streetName} ${streetSuffix}`;
  };

  const constructBannerText2 = () => {
    const status = oldPropData.status || "";
    const price =
      oldPropData.status === "Sold"
        ? oldPropData.salePrice
        : oldPropData.listPrice;
    const formattedPrice = price ? `$${Number(price).toLocaleString()}` : "";
    return `${status} for ${formattedPrice}`;
  };


  const constructCourtesyText1 = () => {
    return `Image courtesy of ${oldPropData.brokerageName || ""}`;
  };

  // Helper function to format status and price for display
  const formatStatusAndPrice = () => {
    const status = oldPropData.status || "";
    const bannerText2 = oldPropData.bannerText2 || "";

    // Determine which price to use based on status
    let price = "";
    if (status === "Sold") {
      price = oldPropData.salePrice;
    } else if (["Active", "Pending", "Contingent"].includes(status)) {
      price = oldPropData.listPrice;
    }

    // Format price with dollar sign and commas
    const formattedPrice = price ? `$${Number(price).toLocaleString()}` : "";

    // Return bannerText2 with formatted price
    return formattedPrice ? `${bannerText2} ${formattedPrice}` : bannerText2;
  };

  // Handle edit current property button click
  const handleEditCurrentProperty = () => {
    setIsFormEditable(true);
    setIsReplaceMode(false);
    setHasTriedToSubmitBuyerPermission(true);
  };

  // Handle replace property button click
  const handleReplaceProperty = () => {
    setIsFormEditable(true);
    setIsReplaceMode(true);
    setHasTriedToSubmitBuyerPermission(true);
    // Call parent function to handle state update through editPropFormData
    if (onReplaceProperty) {
      onReplaceProperty({
        isMemberOfTeam,
        brokerageName: editPropFormData.brokerageName || "",
      });
    }
  };

  // Handle buyer represented checkbox change
  const handleBuyerRepresentedChange = (e) => {
    const isChecked = e.target.checked;
    setIsBuyerRepresented(isChecked);

    if (!isChecked) {
      setBuyerPermissionType("");
    } else {
      // Check if current footerText1 indicates seller representation and update it
      const currentFooterText = editPropFormData.footerText1 || "";
      const sellerPatterns = [
        "I Represented the Seller",
        "I Represented the Sellers",
        "We Represented the Seller",
        "We Represented the Sellers",
      ];

      if (
        sellerPatterns.some((pattern) => currentFooterText.includes(pattern))
      ) {
        const newFooterText = `${
          isMemberOfTeam ? "We" : "I"
        } Represented the Buyer`;
        // Use handleInputChange to update the form data
        handleInputChange({
          target: {
            name: "footerText1",
            value: newFooterText,
          },
        });
      }
    }
  };

  // Handle buyer permission radio button change
  const handleBuyerPermissionChange = (e) => {
    const selectedValue = e.target.value;
    setBuyerPermissionType(selectedValue);

    // Update editPropFormData based on selection
    handleInputChange({
      target: {
        name: "checkedBuyerRepresented",
        value: true,
      },
    });

    handleInputChange({
      target: {
        name: "buyerRepresentedReason",
        value: selectedValue,
      },
    });
  };

  // Logic to disable submit if buyer represented and no permission
  const disableSubmit =
    isBuyerRepresented && buyerPermissionType === "no-permission";

  // Logic to disable submit if buyer represented but no courtesy text
  // Exception: If permission is "with-permission" or "own-rights", courtesy text is not required
  const disableSubmitNoCourtesy =
    isBuyerRepresented &&
    buyerPermissionType !== "with-permission" &&
    buyerPermissionType !== "own-rights" &&
    (!editPropFormData.courtesyText1 ||
      editPropFormData.courtesyText1.trim() === "");

  // Logic to disable submit if buyer represented but no radio button selected
  const disableSubmitNoRadioSelection =
    isBuyerRepresented &&
    buyerPermissionType !== "with-permission" &&
    buyerPermissionType !== "own-rights";

  // Logic to disable submit if footerText2 exceeds 60 characters
  const getTextLength = (value) => (value == null ? "" : String(value)).length;

  const footerText2Length = getTextLength(editPropFormData.footerText2);
  const footerText1Length = getTextLength(editPropFormData.footerText1);

  const footerText2OverLimit = footerText2Length > 60;
  const footerText1OverLimit =
    footerText1Length > REPRESENTATION_TEXT_MAX_LENGTH;

  const disableSubmitOverCharLimit =
    footerText2OverLimit || footerText1OverLimit;

  // Combined disable logic
  const disableSubmitButton =
    disableSubmit ||
    disableSubmitNoCourtesy ||
    disableSubmitNoRadioSelection ||
    disableSubmitOverCharLimit;

  const saveButtonStyle = {
    opacity: disableSubmitButton ? 0.5 : 1,
    backgroundColor: disableSubmitButton ? "#b0b0b0" : "#4e85af",
  };

  const renderSaveButton = (label, oneAreaSubmit) => (
    <button
      type="button"
      className={`photo-edit-form-submit-button${
        disableSubmitButton ? " disabled" : ""
      }`}
      disabled={disableSubmitButton}
      onClick={() => onEditFormSubmit(oneAreaSubmit)}
      style={saveButtonStyle}
    >
      <p>{label}</p>
    </button>
  );

  console.log("editPropFormData", editPropFormData);
  const handleStatusRadioChange = (e) => {
    const newStatus = e.target.value;
    let newBannerText2 = "";
    if (newStatus === "Active") newBannerText2 = "Active Listing | Offered at:";
    else if (newStatus === "Pending")
      newBannerText2 = "Pending Sale | Offered at:";
    else if (newStatus === "Contingent")
      newBannerText2 = "Contingent Sale | Offered at:";
    else if (newStatus === "Sold") newBannerText2 = "Sold | Sale Price:";
    else if (newStatus === "Coming Soon") newBannerText2 = "Coming Soon";

    // Update both fields at once using the new object signature
    handleInputChange({
      status: newStatus,
      bannerText2: newBannerText2,
      ...(newStatus === "Coming Soon"
        ? { listPrice: "", salePrice: "" }
        : {}),
    });
  };

  const handleFooterText1Change = (e) => {
    const value = e.target.value;
    const hasBuyers =
      value.includes("Buyer") || value.includes("Buyers");
    const hasSellers =
      value.includes("Seller") || value.includes("Sellers");

    handleInputChange({
      footerText1: value,
      isBuyingAgent: hasBuyers,
      isSellingAgent: hasSellers || (!hasBuyers && !hasSellers),
      ...(!hasBuyers || hasSellers ? { checkedBuyerRepresented: false } : {}),
    });
  };

  // Handler for price input (listPrice and salePrice) to format with commas but store digit-only
  const handlePriceInputChange = (e) => {
    // Remove all non-digit characters
    const rawValue = e.target.value.replace(/[^0-9]/g, "");
    // Allow empty string if user clears the field, otherwise remove leading zeros
    const cleanValue =
      rawValue === "" ? "" : rawValue.replace(/^0+/, "") || "0";
    handleInputChange({
      [e.target.name]: cleanValue,
    });
  };

  console.log("editPropFormData", editPropFormData);
  let shouldBeChecked =
    editPropFormData.footerText1 ===
    `${isMemberOfTeam ? "We" : "I"} ${
      isMemberOfTeam ? "Are" : "Am"
    } Representing the Sellers`;

  console.log("shouldBeChecked", shouldBeChecked);
  console.log(
    "constructed sentence",
    `${isMemberOfTeam ? "We" : "I"} ${
      isMemberOfTeam ? "Are" : "Am"
    } Representing the Sellers`
  );
  console.log("editPropFormData", editPropFormData.footerText1);
  //console.log("footerText1", footerText1);
  return (
    <Fragment>
      <Dialog
        open={true}
        //onClose={onCloseFn}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xl"
        // full Screen={true}
        fullWidth={true}
        class
        TransitionComponent={Transition}
      >
        <div
          className={`${
            fromPhotoTable
              ? "photo-table-photo-edit-property-container"
              : "photo-edit-property-container"
          }`}
        >
          <div className="photo-edit-property-title-container">
            <p className="photo-edit-property-title">Change Property Details</p>
            <div className="photo-edit-property-title-divider" />
          </div>
          <div
            className="photo-edit-property-form old-props"
            style={{
              opacity: isReplaceMode ? 0.4 : 1,
              transition: "opacity 0.3s ease-in-out",
            }}
          >
            <div className="photo-edit-property-form-title-container">
              <p className="photo-edit-property-form-title-text">
                Current Property Details{" "}
                <span className="long-dash-title">—</span> From the MLS
              </p>
              <div className="photo-edit-property-form-title-divider"></div>
            </div>
            <div className="photo-edit-property-form-body">
              <div className="photo-edit-form-body-col">
                <div className="photo-edit-form-thead property-details-thead">
                  <div className="photo-edit-form-thead-col">
                    <p>Property Details Under Photo</p>
                  </div>
                  <div className="photo-edit-form-thead-col footer-text-thead">
                    <p>Property Representation</p>
                  </div>
                  <div className="photo-edit-form-thead-col photo-courtesy-thead">
                    <p>Photo Courtesy Of:</p>
                  </div>
                  <div className="photo-edit-form-thead-col mls-photo-thead">
                    <p>MLS Photo</p>
                    <p>Shown Here</p>
                  </div>
                </div>
                <div className="photo-edit-form-input">
                  <div className="photo-edit-form-input-col property-details-input-col">
                    <div>
                      <strong>Address:</strong> {oldPropData.bannerText1 || ""}
                    </div>
                    <div className="double-space-margin-top">
                      <strong>Status and Price:</strong>{" "}
                      {formatStatusAndPrice()}
                    </div>
                  </div>
                  <div className="photo-edit-form-input-col footer-text-input-col">
                    <div>
                      <strong>Representation text:</strong>{" "}
                      {oldPropData.footerText1 || ""}
                    </div>
                    {oldPropData.footerText2 && (
                      <div className="double-space-margin-top">
                        <strong>Optional Text:</strong>{" "}
                        {oldPropData.footerText2}
                      </div>
                    )}
                  </div>
                  <div className="photo-edit-form-input-col photo-courtesy-input-col">
                    <div>{oldPropData.courtesyText1 || ""}</div>
                  </div>
                  <div className="photo-edit-form-input-col photo-td mls-photo-input-col">
                    <div className="add-photo-container no-hover">
                      <img src={oldPropData.photoURL} alt="Home" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="photo-edit-property-form new-props">
            <div className="photo-edit-property-form-title-container">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <p className="photo-edit-property-form-title-text">
                  {isReplaceMode
                    ? "REPLACE THE ABOVE PROPERTY WITH A NEW PROPERTY"
                    : "MAKE CHANGES TO THE ABOVE PROPERTY HERE"}
                </p>
                {isFormEditable && (
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      marginRight: "100px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleEditCurrentProperty}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#00558f",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "bold",
                        opacity: 1,
                        transition: "opacity 0.15s ease-in-out",
                      }}
                    >
                      Click here to EDIT the property shown above
                    </button>
                    <button
                      type="button"
                      onClick={handleReplaceProperty}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#dc3545",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "bold",
                        opacity: 1,
                        transition: "opacity 0.15s ease-in-out",
                      }}
                    >
                      Click here to REPLACE the property shown above
                    </button>
                  </div>
                )}
              </div>
              <div className="photo-edit-property-form-title-divider"></div>
            </div>
            <div className="photo-edit-property-form-body">
              <div className="photo-edit-form-body-col">
                {!isFormEditable ? (
                  // Show centered buttons when form is not editable
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "200px",
                      gap: "10px",
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleEditCurrentProperty}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#00558f",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "bold",
                        opacity: isFlashing ? 0.3 : 1,
                        transition: "opacity 0.15s ease-in-out",
                      }}
                    >
                      Click here to EDIT the property shown above
                    </button>
                    <button
                      type="button"
                      onClick={handleReplaceProperty}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#dc3545",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "bold",
                        opacity: isFlashing ? 0.3 : 1,
                        transition: "opacity 0.15s ease-in-out",
                      }}
                    >
                      Click here to REPLACE the property shown above
                    </button>
                  </div>
                ) : (
                  // Show full table when form is editable
                  <>
                    <div className="photo-edit-form-thead">
                      <div className="photo-edit-form-thead-col property-details-thead">
                        <p>Property Details Under Photo</p>
                      </div>
                      <div className="photo-edit-form-thead-col footer-text-thead">
                        <p>Property Representation</p>
                      </div>
                      <div className="photo-edit-form-thead-col photo-courtesy-thead">
                        <p>Photo Courtesy Of:</p>
                      </div>
                      <div className="photo-edit-form-thead-col mls-photo-thead">
                        <p>Photo</p>
                      </div>
                    </div>
                    <div className="photo-edit-form-input">
                      <div className="photo-edit-form-input-col property-details-input-col">
                        <input
                          className="property-details-input"
                          type="text"
                          id="bannerText1"
                          name="bannerText1"
                          value={
                            isReplaceMode
                              ? editPropFormData.bannerText1 || ""
                              : editPropFormData.bannerText1 ||
                                constructBannerText1()
                          }
                          onChange={handleInputChange}
                          placeholder="Address"
                          disabled={!isFormEditable}
                          style={{
                            opacity: isFormEditable ? 1 : 0.6,
                            backgroundColor: isFormEditable
                              ? "white"
                              : "#f5f5f5",
                          }}
                        ></input>

                        {/* Property Status Radio Buttons */}
                        <div
                          className="property-status-container"
                          style={{
                            marginTop: "10px",
                            textAlign: "left",
                          }}
                        >
                          <div
                            className="property-status-option"
                            style={{ textAlign: "left" }}
                          >
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "5px",
                                justifyContent: "flex-start",
                              }}
                            >
                              <input
                                type="radio"
                                name="status"
                                value="Active"
                                checked={editPropFormData.status === "Active"}
                                onChange={handleStatusRadioChange}
                                disabled={!isFormEditable}
                                style={{
                                  marginRight: "8px",
                                  minWidth: "16px",
                                  opacity: isFormEditable ? 1 : 0.6,
                                }}
                              />
                              <span
                                style={{
                                  color: isFormEditable ? "#333" : "#999",
                                }}
                              >
                                Active Listing
                              </span>
                            </label>
                          </div>
                          <div
                            className="property-status-option"
                            style={{ textAlign: "left" }}
                          >
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "5px",
                                justifyContent: "flex-start",
                              }}
                            >
                              <input
                                type="radio"
                                name="status"
                                value="Pending"
                                checked={editPropFormData.status === "Pending"}
                                onChange={handleStatusRadioChange}
                                disabled={!isFormEditable}
                                style={{
                                  marginRight: "8px",
                                  minWidth: "16px",
                                  opacity: isFormEditable ? 1 : 0.6,
                                }}
                              />
                              <span
                                style={{
                                  color: isFormEditable ? "#333" : "#999",
                                }}
                              >
                                Pending Sale
                              </span>
                            </label>
                          </div>
                          <div
                            className="property-status-option"
                            style={{ textAlign: "left" }}
                          >
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "5px",
                                justifyContent: "flex-start",
                              }}
                            >
                              <input
                                type="radio"
                                name="status"
                                value="Contingent"
                                checked={
                                  editPropFormData.status === "Contingent"
                                }
                                onChange={handleStatusRadioChange}
                                disabled={!isFormEditable}
                                style={{
                                  marginRight: "8px",
                                  minWidth: "16px",
                                  opacity: isFormEditable ? 1 : 0.6,
                                }}
                              />
                              <span
                                style={{
                                  color: isFormEditable ? "#333" : "#999",
                                }}
                              >
                                Contingent Sale
                              </span>
                            </label>
                          </div>
                          <div
                            className="property-status-option"
                            style={{ textAlign: "left" }}
                          >
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "5px",
                                justifyContent: "flex-start",
                              }}
                            >
                              <input
                                type="radio"
                                name="status"
                                value="Sold"
                                checked={editPropFormData.status === "Sold"}
                                onChange={handleStatusRadioChange}
                                disabled={!isFormEditable}
                                style={{
                                  marginRight: "8px",
                                  minWidth: "16px",
                                  opacity: isFormEditable ? 1 : 0.6,
                                }}
                              />
                              <span
                                style={{
                                  color: isFormEditable ? "#333" : "#999",
                                }}
                              >
                                Sold
                              </span>
                            </label>
                          </div>
                          <div
                            className="property-status-option"
                            style={{ textAlign: "left" }}
                          >
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "5px",
                                justifyContent: "flex-start",
                              }}
                            >
                              <input
                                type="radio"
                                name="status"
                                value="Coming Soon"
                                checked={editPropFormData.status === "Coming Soon"}
                                onChange={handleStatusRadioChange}
                                disabled={!isFormEditable}
                                style={{
                                  marginRight: "8px",
                                  minWidth: "16px",
                                  opacity: isFormEditable ? 1 : 0.6,
                                }}
                              />
                              <span
                                style={{
                                  color: isFormEditable ? "#333" : "#999",
                                }}
                              >
                                Coming Soon
                              </span>
                            </label>
                          </div>
                        </div>

                        {/* BannerText2 and Price Input: Only show if status is selected */}
                        {editPropFormData.status && (
                          <div className="banner-text2-container">
                            <input
                              className="property-details-input"
                              type="text"
                              id="bannerText2"
                              name="bannerText2"
                              value={
                                isReplaceMode
                                  ? editPropFormData.bannerText2 || ""
                                  : editPropFormData.bannerText2 ||
                                    constructBannerText2()
                              }
                              onChange={handleInputChange}
                              placeholder="Status and Price"
                              disabled={!isFormEditable}
                              style={{
                                opacity: isFormEditable ? 1 : 0.6,
                                backgroundColor: isFormEditable
                                  ? "white"
                                  : "#f5f5f5",
                              }}
                            />
                            {editPropFormData.status !== "Coming Soon" && (
                              <div className="price-input-container">
                                <span style={{ fontWeight: "bold" }}>$</span>
                                <input
                                  className="property-details-input"
                                  type="text"
                                  id="priceInput"
                                  name="priceInput"
                                  value={
                                    editPropFormData.status === "Sold"
                                      ? editPropFormData.salePrice !==
                                          undefined &&
                                        editPropFormData.salePrice !== null &&
                                        editPropFormData.salePrice !== "" &&
                                        editPropFormData.salePrice !== "0"
                                        ? Number(
                                            editPropFormData.salePrice
                                          ).toLocaleString()
                                        : ""
                                      : editPropFormData.listPrice !==
                                          undefined &&
                                        editPropFormData.listPrice !== null &&
                                        editPropFormData.listPrice !== "" &&
                                        editPropFormData.listPrice !== "0"
                                      ? Number(
                                          editPropFormData.listPrice
                                        ).toLocaleString()
                                      : ""
                                  }
                                  onChange={(e) => {
                                    // Use the handler for digit-only storage
                                    if (editPropFormData.status === "Sold") {
                                      handlePriceInputChange({
                                        target: {
                                          name: "salePrice",
                                          value: e.target.value,
                                        },
                                      });
                                    } else {
                                      handlePriceInputChange({
                                        target: {
                                          name: "listPrice",
                                          value: e.target.value,
                                        },
                                      });
                                    }
                                  }}
                                  placeholder={
                                    editPropFormData.status === "Sold"
                                      ? "Sale Price"
                                      : "List Price"
                                  }
                                  disabled={!isFormEditable}
                                  style={{
                                    opacity: isFormEditable ? 1 : 0.6,
                                    backgroundColor: isFormEditable
                                      ? "white"
                                      : "#f5f5f5",
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="photo-edit-form-input-col footer-text-input-col">
                        {editPropFormData.status && (
                          <div style={{ marginBottom: 8 }}>
                            <input
                              className="footer-text-input"
                              type="text"
                              id="footerText1"
                              name="footerText1"
                              value={editPropFormData.footerText1 ?? ""}
                              onChange={handleFooterText1Change}
                              placeholder="Property Representation"
                              disabled={!isFormEditable}
                              maxLength={REPRESENTATION_TEXT_MAX_LENGTH}
                              style={{
                                opacity: isFormEditable ? 1 : 0.6,
                                backgroundColor: isFormEditable
                                  ? "white"
                                  : "#f5f5f5",
                                color: footerText1OverLimit
                                  ? "#dc3545"
                                  : "inherit",
                                width: "200px",
                              }}
                            />
                            <div
                              style={{
                                fontSize: "11px",
                                marginTop: "4px",
                                color: footerText1OverLimit ? "#dc3545" : "#666",
                                fontStyle: "italic",
                              }}
                            >
                              {footerText1Length}/{REPRESENTATION_TEXT_MAX_LENGTH}{" "}
                              characters
                            </div>
                            {footerText1OverLimit && (
                              <div
                                style={{
                                  fontSize: "11px",
                                  marginTop: "4px",
                                  color: "#dc3545",
                                  fontWeight: 500,
                                }}
                              >
                                Property Representation cannot exceed{" "}
                                {REPRESENTATION_TEXT_MAX_LENGTH} characters
                              </div>
                            )}
                          </div>
                        )}
                        <div>
                          <input
                            className="footer-text-input"
                            type="text"
                            id="footerText2"
                            name="footerText2"
                            value={editPropFormData.footerText2 || ""}
                            onChange={handleInputChange}
                            placeholder="Optional Text"
                            disabled={!isFormEditable}
                            maxLength={60}
                            style={{
                              opacity: isFormEditable ? 1 : 0.6,
                              backgroundColor: isFormEditable
                                ? "white"
                                : "#f5f5f5",
                              color: footerText2OverLimit ? "#dc3545" : "inherit",
                              width: "200px",
                            }}
                          ></input>
                          <div
                            style={{
                              fontSize: "11px",
                              marginTop: "4px",
                              color: footerText2OverLimit ? "#dc3545" : "#666",
                              fontStyle: "italic",
                            }}
                          >
                            {footerText2Length}/60 characters
                          </div>
                          {footerText2OverLimit && (
                            <div
                              style={{
                                fontSize: "11px",
                                marginTop: "4px",
                                color: "#dc3545",
                                fontWeight: 500,
                              }}
                            >
                              Optional Text cannot exceed 60 characters
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="photo-edit-form-input-col photo-courtesy-input-col">
                        {(isBuyerRepresented ||
                          (editPropFormData.footerText1 &&
                            (editPropFormData.footerText1.includes("Buyer") ||
                              editPropFormData.footerText1.includes(
                                "Buyers"
                              )))) && (
                          <div>
                            <input
                              className="photo-courtesy-input"
                              type="text"
                              id="courtesyText1"
                              name="courtesyText1"
                              value={
                                editPropFormData.courtesyText1 !== undefined &&
                                editPropFormData.courtesyText1 !== null
                                  ? editPropFormData.courtesyText1
                                  : isReplaceMode
                                  ? `Image courtesy of ${
                                      editPropFormData.brokerageName || ""
                                    }`
                                  : constructCourtesyText1()
                              }
                              onChange={handleInputChange}
                              placeholder="Courtesy Text 1"
                              disabled={!isFormEditable}
                              style={{
                                opacity: isFormEditable ? 1 : 0.6,
                                backgroundColor: isFormEditable
                                  ? "white"
                                  : "#f5f5f5",
                                color: "inherit",
                              }}
                            ></input>
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#666",
                                marginTop: "4px",
                                fontStyle: "italic",
                                opacity: isFormEditable ? 1 : 0.6,
                              }}
                            >
                              {
                                (
                                  (editPropFormData.courtesyText1 !==
                                    undefined &&
                                  editPropFormData.courtesyText1 !== null
                                    ? editPropFormData.courtesyText1
                                    : isReplaceMode
                                    ? `Image courtesy of ${
                                        editPropFormData.brokerageName || ""
                                      }`
                                    : constructCourtesyText1()) || ""
                                ).length
                              }{" "}
                              characters
                            </div>
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#666",
                                marginTop: "4px",
                                fontStyle: "italic",
                                opacity: isFormEditable ? 1 : 0.6,
                              }}
                            >
                              Example: Image courtesy of {brokerageName}
                            </div>
                          </div>
                        )}

                        {/* Buyer Permission Radio Buttons - Only show if footerText1 contains "Buyer" or "Buyers" */}
                        {editPropFormData.footerText1 &&
                          (editPropFormData.footerText1.includes("Buyer") ||
                            editPropFormData.footerText1.includes(
                              "Buyers"
                            )) && (
                            <div className="buyer-permission-container">
                              <div className="buyer-permission-option">
                                <label className="buyer-represented-radio-label">
                                  <input
                                    type="radio"
                                    name="buyerPermission"
                                    value="with-permission"
                                    checked={
                                      buyerPermissionType === "with-permission"
                                    }
                                    onChange={handleBuyerPermissionChange}
                                    disabled={!isFormEditable}
                                    className="buyer-represented-radio"
                                    style={{
                                      opacity: isFormEditable ? 1 : 0.6,
                                    }}
                                  />
                                  <span
                                    className="buyer-represented-radio-text"
                                    style={{
                                      color: isFormEditable ? "#333" : "#999",
                                    }}
                                  >
                                    I represented the Buyer and I have
                                    permission from the listing agent to use the
                                    photo
                                  </span>
                                </label>
                              </div>
                              <div className="buyer-permission-option">
                                <label className="buyer-represented-radio-label">
                                  <input
                                    type="radio"
                                    name="buyerPermission"
                                    value="own-rights"
                                    checked={
                                      buyerPermissionType === "own-rights"
                                    }
                                    onChange={handleBuyerPermissionChange}
                                    disabled={!isFormEditable}
                                    className="buyer-represented-radio"
                                    style={{
                                      opacity: isFormEditable ? 1 : 0.6,
                                    }}
                                  />
                                  <span
                                    className="buyer-represented-radio-text"
                                    style={{
                                      color: isFormEditable ? "#333" : "#999",
                                    }}
                                  >
                                    I represented the Buyer and I own the rights
                                    to the photo (No listing agent permission
                                    required)
                                  </span>
                                </label>
                              </div>
                              <div className="buyer-permission-option">
                                <label className="buyer-represented-radio-label">
                                  <input
                                    type="radio"
                                    name="buyerPermission"
                                    value="no-permission"
                                    checked={
                                      buyerPermissionType === "no-permission"
                                    }
                                    onChange={handleBuyerPermissionChange}
                                    disabled={!isFormEditable}
                                    className="buyer-represented-radio"
                                    style={{
                                      opacity: isFormEditable ? 1 : 0.6,
                                    }}
                                  />
                                  <span
                                    className="buyer-represented-radio-text"
                                    style={{
                                      color: isFormEditable ? "#333" : "#999",
                                    }}
                                  >
                                    I represented the Buyer and I don't have
                                    permission
                                  </span>
                                </label>
                              </div>
                            </div>
                          )}
                      </div>

                      <div className="photo-edit-form-input-col photo-td mls-photo-input-col">
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          {editPropFormData.justAdded && (
                            <div className="mls-photo-input">
                              <div className="add-photo-container no-hover">
                                <img
                                  src={editPropFormData.optimizedPhotoURL}
                                  alt="Home"
                                />
                              </div>
                            </div>
                          )}
                          {editPropFormData.justAdded ? (
                            <button
                              type="button"
                              onClick={
                                isFormEditable
                                  ? () =>
                                      openUploadMedia(
                                        oldPropData.displayOrder,
                                        editPropFormData.photoURL,
                                      )
                                  : undefined
                              }
                              disabled={!isFormEditable}
                              style={{
                                ...alternatePhotoButtonStyle,
                                opacity: isFormEditable ? 1 : 0.6,
                                cursor: isFormEditable
                                  ? "pointer"
                                  : "not-allowed",
                              }}
                            >
                              Click to Upload Alternate Photo
                            </button>
                          ) : (
                            <div
                              className="add-photo-container add-prop mls-photo-input"
                              onClick={
                                isFormEditable
                                  ? () =>
                                      openUploadMedia(
                                        oldPropData.displayOrder,
                                        editPropFormData.photoURL,
                                      )
                                  : undefined
                              }
                              style={{
                                opacity: isFormEditable ? 1 : 0.6,
                                cursor: isFormEditable
                                  ? "pointer"
                                  : "not-allowed",
                              }}
                            >
                              <p>Click to</p>
                              <p>Upload Photo</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div
            className="photo-edit-form-button-container"
            style={{ justifyContent: "center", gap: "20px" }}
          >
            <button
              type="button"
              className="photo-edit-form-cancel-button"
              onClick={onCloseFn}
            >
              <p>Cancel</p>
            </button>
            {showAllPropertiesButton &&
              renderSaveButton("Save for All Areas", false)}
            {showButton && renderSaveButton("Save for This Area Only", true)}
          </div>
          {disableSubmit && (
            <div
              style={{
                color: "#dc3545",
                textAlign: "center",
                marginTop: "8px",
                marginBottom: "10px",
                fontWeight: 500,
              }}
            >
              You must have photo permission if you represented the Buyer
            </div>
          )}
          {disableSubmitNoCourtesy && (
            <div
              style={{
                color: "#dc3545",
                textAlign: "center",
                marginTop: "8px",
                marginBottom: "10px",
                fontWeight: 500,
              }}
            >
              Buyer represented properties must have an Image Courtesy
            </div>
          )}
          {disableSubmitNoRadioSelection && hasTriedToSubmitBuyerPermission && (
            <div
              style={{
                color: "#dc3545",
                textAlign: "center",
                marginTop: "8px",
                marginBottom: "10px",
                fontWeight: 500,
              }}
            >
              Please select a permission option for buyer representation
            </div>
          )}
        </div>
      </Dialog>
    </Fragment>
  );
};

export default compose(
  withSnackbar
)(PropHighlightsEditProperty);
